/**
 * Downloadable CSV templates for Settings → Data Imports.
 *
 * The column headers here must stay in sync with the ApexOS template presets
 * in backend/apexos/apps/imports/pipeline/presets.py — uploading a filled-in
 * template is auto-detected by those presets and pre-mapped, so users never
 * touch the column-mapping step.
 */

export interface ImportTemplate {
  key: 'contacts' | 'opportunities' | 'jobs';
  label: string;
  filename: string;
  headers: string[];
  /** One example row shown in the file so formats are obvious. */
  example: string[];
}

export const IMPORT_TEMPLATES: ImportTemplate[] = [
  {
    key: 'contacts',
    label: 'Contacts Template',
    filename: 'apexos-contacts-template.csv',
    headers: [
      'First Name', 'Last Name', 'Email', 'Phone', 'Secondary Email',
      'Secondary Phone', 'Company', 'Title', 'Website', 'Street Address',
      'Street Address 2', 'City', 'State', 'Zip', 'Country', 'Tags',
      'Source', 'Assigned To', 'Lead Date',
    ],
    example: [
      'John', 'Smith', 'john.smith@example.com', '(570) 555-0142', '',
      '(570) 555-0143', 'Smith Properties LLC', 'Owner', 'https://example.com',
      '123 Main St', '', 'Scranton', 'PA', '18503', 'USA',
      'referral; repeat-customer', 'Website', 'rep@yourcompany.com', '2026-01-15',
    ],
  },
  {
    key: 'opportunities',
    label: 'Opportunities Template',
    filename: 'apexos-opportunities-template.csv',
    headers: [
      'First Name', 'Last Name', 'Email', 'Phone', 'Opportunity Name',
      'Claim Number', 'Policy Number', 'Insurance Company', 'Deductible', 'Date of Loss', 'Gate Code', 'Gated Community',
      'Description', 'Estimated Value', 'Stage', 'Owner', 'Street Address',
      'Street Address 2', 'City', 'State', 'Zip',
    ],
    example: [
      'John', 'Smith', 'john.smith@example.com', '(570) 555-0142',
      'Smith Residence - Full Roof Replacement', '', '', '', '', '', '', '', 'Asphalt shingle tear-off and replace',
      '18500', 'New Lead', 'rep@yourcompany.com', '123 Main St', '',
      'Scranton', 'PA', '18503',
    ],
  },
  {
    key: 'jobs',
    label: 'Jobs Template',
    filename: 'apexos-jobs-template.csv',
    headers: [
      'First Name', 'Last Name', 'Email', 'Phone', 'Job Description',
      'Job Value', 'Claim Number', 'Policy Number', 'Insurance Company',
      'Deductible Amount', 'Date of Loss', 'Stage', 'Sales Rep',
      'Street Address', 'Street Address 2', 'City', 'State', 'Zip', 'Tags',
    ],
    example: [
      'John', 'Smith', 'john.smith@example.com', '(570) 555-0142',
      'Hail damage - full replacement', '18500', 'CLM-004521', 'HO-99182',
      'State Farm', '1500', '2026-05-02', 'Scheduled', 'rep@yourcompany.com',
      '123 Main St', '', 'Scranton', 'PA', '18503', 'insurance; hail',
    ],
  },
];

const csvEscape = (value: string) =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

export function downloadImportTemplate(template: ImportTemplate): void {
  const lines = [
    template.headers.map(csvEscape).join(','),
    template.example.map(csvEscape).join(','),
  ];
  const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = template.filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
